import { useState, useEffect, useCallback } from 'react';

import { Link } from 'react-router-dom';

import { Calendar, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';

import { Card, CardContent } from '../ui/card';

import { Badge } from '../ui/badge';

import { Button } from '../ui/button';

import axios from 'axios';

import {

  getNewsArticlePath,

  NEWS_FALLBACK_PREVIEW,

  NATIONAL_NEWS_FALLBACK,

  NEWS_SOURCE,

  NEWS_SOURCE_OPTIONS,

  filterNewsBySource,

  isExternalNewsArticle,
  getExternalNewsUrl,

  sortNewsByDateDesc,

} from '../../utils/newsArticle';



const API = `${process.env.REACT_APP_BACKEND_URL}/api`;



const categoryColors = {

  centenary: 'bg-countryside-terracotta text-white',

  planning: 'bg-amber-500 text-white',

  projects: 'bg-countryside-green text-white',

  general: 'bg-stone-500 text-white',

};



export const LatestNews = () => {

  const [news, setNews] = useState([]);

  const [filter, setFilter] = useState('all');

  const [sourceFilter, setSourceFilter] = useState('local');

  const [loading, setLoading] = useState(true);

  const [currentSlide, setCurrentSlide] = useState(0);

  const [isAutoPlaying, setIsAutoPlaying] = useState(true);



  useEffect(() => {

    const fetchNews = async () => {

      try {

        const response = await axios.get(`${API}/news`, { params: { limit: 50 } });

        setNews(sortNewsByDateDesc(response.data || []));

      } catch (error) {

        console.error('Failed to fetch news:', error);

        setNews(sortNewsByDateDesc([...NEWS_FALLBACK_PREVIEW, ...NATIONAL_NEWS_FALLBACK]));

      } finally {

        setLoading(false);

      }

    };

    fetchNews();

  }, []);



  const sourceFilteredNews = filterNewsBySource(news, sourceFilter);

  const filteredNews =

    filter === 'all' ? sourceFilteredNews : sourceFilteredNews.filter((article) => article.category === filter);

  const displayedNews = sortNewsByDateDesc(
    filter === 'all' ? sourceFilteredNews : filteredNews
  ).slice(0, 3);



  useEffect(() => {

    setCurrentSlide(0);

  }, [filter, sourceFilter, displayedNews.length]);



  const nextSlide = useCallback(() => {

    if (displayedNews.length <= 1) return;

    setCurrentSlide((prev) => (prev + 1) % displayedNews.length);

  }, [displayedNews.length]);



  const prevSlide = useCallback(() => {

    if (displayedNews.length <= 1) return;

    setCurrentSlide((prev) => (prev - 1 + displayedNews.length) % displayedNews.length);

  }, [displayedNews.length]);



  useEffect(() => {

    if (!isAutoPlaying || displayedNews.length <= 1) return;



    const interval = setInterval(nextSlide, 3000);

    return () => clearInterval(interval);

  }, [isAutoPlaying, nextSlide, displayedNews.length]);



  const formatDate = (dateStr) => {

    return new Date(dateStr).toLocaleDateString('en-GB', {

      day: 'numeric',

      month: 'short',

      year: 'numeric',

    });

  };



  const renderNewsCard = (article, index) => {
    const card = (
      <Card
        className="bg-white border-stone-200 overflow-hidden group h-full hover:shadow-lg transition-shadow duration-300 max-w-md lg:max-w-lg mx-auto"
        data-testid={`news-card-${index}`}
      >
        <div className="aspect-[16/10] overflow-hidden">
          <img
            src={article.image_url || '/images/cpre images for website/5.jpg'}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <CardContent className="p-5">
          <div className="flex items-center gap-3 mb-3">
            <Badge className={`${categoryColors[article.category] || categoryColors.general} rounded-full text-xs font-medium`}>
              {article.category}
            </Badge>
            <span className="text-sm text-muted-foreground flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {formatDate(article.created_at)}
            </span>
          </div>
          <h3 className="font-heading font-semibold text-base text-foreground mb-1.5 line-clamp-2 group-hover:text-countryside-green transition-colors duration-200">
            {article.title}
          </h3>
          <p className="text-muted-foreground text-sm line-clamp-2">
            {article.excerpt}
          </p>
        </CardContent>
      </Card>
    );

    if (isExternalNewsArticle(article)) {
      return (
        <a href={getExternalNewsUrl(article)} target="_blank" rel="noopener noreferrer">
          {card}
        </a>
      );
    }

    return <Link to={getNewsArticlePath(article)}>{card}</Link>;
  };



  return (

    <section className="section-padding" data-testid="latest-news">

      <div className="container-custom">

        {/* Header */}

        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">

          <div>

            <span className="text-countryside-terracotta font-medium text-sm uppercase tracking-wider">Stay Informed</span>

            <h2 className="mt-2">Latest News</h2>

            <p className="text-muted-foreground mt-2 max-w-xl">

              Keep up to date with countryside news, planning updates, and conservation stories from across Rutland and Lincolnshire.

            </p>

          </div>

          

          {/* Category Filters */}

          <div className="flex flex-col gap-3 items-start md:items-end">

            <div className="flex gap-1 p-1 bg-stone-100 rounded-full" data-testid="news-source-toggle">

              {NEWS_SOURCE_OPTIONS.map((option) => (

                <Button

                  key={option.id}

                  variant={sourceFilter === option.id ? 'default' : 'ghost'}

                  size="sm"

                  onClick={() => setSourceFilter(option.id)}

                  className={`rounded-full px-4 ${

                    sourceFilter === option.id

                      ? 'bg-countryside-green hover:bg-countryside-green-dark'

                      : 'text-stone-600 hover:text-foreground'

                  }`}

                  data-testid={`source-filter-${option.id}`}

                >

                  {option.label}

                </Button>

              ))}

            </div>



            <div className="flex gap-2 flex-wrap" data-testid="news-filters">

            {['all', 'centenary', 'planning', 'projects'].map((cat) => (

              <Button

                key={cat}

                variant={filter === cat ? 'default' : 'outline'}

                size="sm"

                onClick={() => setFilter(cat)}

                className={`rounded-full capitalize ${

                  filter === cat 

                    ? 'bg-countryside-green hover:bg-countryside-green-dark' 

                    : 'border-stone-300'

                }`}

                data-testid={`filter-${cat}`}

              >

                {cat === 'all' ? 'All News' : cat}

              </Button>

            ))}

            </div>

          </div>

        </div>



        {/* News Slideshow Carousel */}

        {!loading && displayedNews.length > 0 && (

          <div

            className="relative"

            data-testid="latest-news-carousel"

            onMouseEnter={() => setIsAutoPlaying(false)}

            onMouseLeave={() => setIsAutoPlaying(true)}

          >

            <div className="relative mx-auto w-full max-w-md lg:max-w-lg">

              <div className="relative min-h-[300px]">

                {displayedNews.map((article, index) => (

                  <div

                    key={article.id}

                    className={`transition-all duration-700 ease-in-out ${

                      index === currentSlide

                        ? 'opacity-100 translate-x-0 relative z-10'

                        : 'opacity-0 translate-x-8 absolute inset-0 z-0 pointer-events-none'

                    }`}

                    aria-hidden={index !== currentSlide}

                  >

                    {renderNewsCard(article, index)}

                  </div>

                ))}

              </div>



              {displayedNews.length > 1 && (

                <>

                  <button

                    type="button"

                    onClick={prevSlide}

                    className="absolute left-0 top-[42%] -translate-y-1/2 -translate-x-1/2 z-20 w-9 h-9 rounded-full bg-white border border-stone-200 shadow-md flex items-center justify-center text-countryside-green hover:bg-countryside-green hover:text-white transition-colors"

                    aria-label="Previous news article"

                    data-testid="latest-news-prev"

                  >

                    <ChevronLeft className="w-5 h-5" />

                  </button>

                  <button

                    type="button"

                    onClick={nextSlide}

                    className="absolute right-0 top-[42%] -translate-y-1/2 translate-x-1/2 z-20 w-9 h-9 rounded-full bg-white border border-stone-200 shadow-md flex items-center justify-center text-countryside-green hover:bg-countryside-green hover:text-white transition-colors"

                    aria-label="Next news article"

                    data-testid="latest-news-next"

                  >

                    <ChevronRight className="w-5 h-5" />

                  </button>

                </>

              )}

            </div>



            {displayedNews.length > 1 && (

              <div className="flex justify-center gap-2 mt-5">

                  {displayedNews.map((article, index) => (

                    <button

                      key={article.id}

                      type="button"

                      onClick={() => setCurrentSlide(index)}

                      className={`h-2.5 rounded-full transition-all duration-300 ${

                        index === currentSlide

                          ? 'w-8 bg-countryside-green'

                          : 'w-2.5 bg-stone-300 hover:bg-stone-400'

                      }`}

                      aria-label={`Go to news slide ${index + 1}`}

                      data-testid={`latest-news-dot-${index}`}

                    />

                  ))}

              </div>

            )}

          </div>

        )}



        {/* View All Link */}

        <div className="text-center mt-8">

          <Link to="/news">

            <Button 

              variant="outline" 

              size="lg"

              className="border-countryside-green text-countryside-green hover:bg-countryside-green hover:text-white rounded-full px-8"

              data-testid="view-all-news"

            >

              View All News

              <ArrowRight className="w-4 h-4 ml-2" />

            </Button>

          </Link>

        </div>

      </div>

    </section>

  );

};


